import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, Upload, ShieldCheck, Clock, X as XIcon, FileText, CreditCard } from "lucide-react";

export default function Verification() {
  const { user, checkAuth } = useAuth();
  const [state, setState] = useState({ items: [], is_verified: false, verified_until: null, verified_price: 49 });
  const [subs, setSubs] = useState([]);
  const [docPath, setDocPath] = useState("");
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [insurance, setInsurance] = useState("");
  const [policy, setPolicy] = useState("");
  const [chNumber, setChNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/verifications/mine").then(({ data }) => setState(data));
  const loadSubs = () => api.get("/subscriptions/mine").then(({ data }) => setSubs(data.items || [])).catch(() => setSubs([]));
  useEffect(() => { load(); loadSubs(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocPath(data.path);
      setDocName(f.name);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!docPath) { toast.error("Please upload proof of insurance"); return; }
    setSubmitting(true);
    try {
      await api.post("/verifications", {
        doc_path: docPath,
        insurance_provider: insurance || null,
        policy_number: policy || null,
        companies_house_number: chNumber || null,
      });
      toast.success("Submitted — an admin will review shortly");
      setDocPath(""); setDocName(""); setInsurance(""); setPolicy(""); setChNumber("");
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const payNow = async (verification_id) => {
    setPaying(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        lookup_key: "verified_annual",
        target_id: verification_id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setPaying(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start payment");
      setPaying(false);
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const cancelAutoRenew = async (sub_id) => {
    if (!window.confirm("Cancel auto-renew? Your Verified badge stays active until the end of the current period.")) return;
    setCancelling(true);
    try {
      await api.post(`/subscriptions/${sub_id}/cancel`);
      toast.success("Auto-renew turned off — your badge stays active until the period ends.");
      await loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally { setCancelling(false); }
  };

  const eligible = user?.user_type === "business" || user?.user_type === "removal_company";
  const pending = state.items?.find((i) => i.status === "pending");
  const awaiting = state.items?.find((i) => i.status === "approved_awaiting_payment");
  const price = state.verified_price ?? 49;
  const untilDate = state.verified_until ? new Date(state.verified_until) : null;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Trust</div>
      <div className="mt-2 flex items-center gap-3">
        <h1 className="font-display font-black text-4xl tracking-tighter text-forest">Get verified</h1>
        {state.is_verified && (
          <span data-testid="verified-status-badge" className="inline-flex items-center gap-1.5 bg-forest text-white overline text-xs px-3 py-1.5">
            <BadgeCheck className="w-4 h-4" /> Verified
          </span>
        )}
      </div>
      <p className="mt-3 text-muted-foreground max-w-xl">
        Upload your proof of insurance. Once approved by our team, a &quot;Verified ✓&quot; badge appears on all
        your listings and profile for a full year — a strong trust signal for buyers.
        {' '}
        <span className="block mt-1 text-sm">Annual fee: <b className="text-forest">£{Number(price).toFixed(2)}</b>.</span>
      </p>

      {!eligible ? (
        <div className="mt-8 border border-border bg-white p-8">
          <div className="overline text-muted-foreground">Not eligible</div>
          <div className="mt-2 font-display text-xl tracking-tight text-forest">Verification is for businesses &amp; removal firms</div>
          <p className="mt-2 text-sm text-muted-foreground">Individual accounts can browse and buy freely — verification is for sellers offering commercial services.</p>
        </div>
      ) : state.is_verified ? (
        <div className="mt-8 border border-forest bg-sage-light p-8 flex items-start gap-4" data-testid="verified-panel">
          <ShieldCheck className="w-8 h-8 text-forest shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl tracking-tight text-forest">You&apos;re verified ✓</div>
            <p className="mt-2 text-sm text-muted-foreground">The Verified badge is now visible on all your listings, requests and company profile.</p>
            {(() => {
              const verifiedSub = subs.find((s) => s.kind === "verified" && ["active", "trialing", "past_due"].includes(s.status));
              return (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {untilDate && (
                    <div className="inline-flex items-center gap-2 bg-white border border-border px-3 py-1.5 overline text-xs" data-testid="verified-renewal-chip">
                      {verifiedSub?.cancel_at_period_end ? "Ends" : "Renews"}{" "}
                      {untilDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  )}
                  {verifiedSub && !verifiedSub.cancel_at_period_end && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={cancelling}
                      onClick={() => cancelAutoRenew(verifiedSub.stripe_subscription_id)}
                      data-testid="verified-cancel-btn"
                      className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-9 px-4"
                    >
                      {cancelling ? "Cancelling…" : "Cancel auto-renew"}
                    </Button>
                  )}
                  {verifiedSub?.cancel_at_period_end && (
                    <Badge className="rounded-none bg-safety text-white overline text-[10px]">Auto-renew off</Badge>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      ) : awaiting ? (
        <div className="mt-8 border-2 border-safety bg-white p-8" data-testid="awaiting-payment-panel">
          <div className="flex items-start gap-4">
            <BadgeCheck className="w-8 h-8 text-forest shrink-0" />
            <div className="flex-1">
              <div className="overline text-safety">Approved — one step left</div>
              <div className="mt-1 font-display text-2xl tracking-tight text-forest">Activate your Verified badge</div>
              <p className="mt-2 text-sm text-muted-foreground">
                Your documents were approved on {new Date(awaiting.reviewed_at || awaiting.submitted_at).toLocaleDateString("en-GB")}.
                Pay <b>£{Number(price).toFixed(2)}</b> to activate your Verified ✓ badge for a full year.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-forest">
                <li>✓ Verified badge on every listing, request & profile</li>
                <li>✓ Priority placement in buyer searches</li>
                <li>✓ Access to the &quot;Verified only&quot; filter</li>
                <li>✓ 12-month coverage — one-off annual fee</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div>
              <div className="overline text-muted-foreground">Total today</div>
              <div className="font-display text-3xl tabular-nums text-forest">£{Number(price).toFixed(2)}<span className="text-sm text-muted-foreground font-body ml-1">/ year</span></div>
            </div>
            <Button
              onClick={() => payNow(awaiting.verification_id)}
              disabled={paying}
              data-testid="verification-pay-btn"
              className="rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-8 inline-flex items-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              {paying ? "Redirecting…" : `Pay £${Number(price).toFixed(0)} & activate →`}
            </Button>
          </div>
        </div>
      ) : pending ? (
        <div className="mt-8 border border-safety bg-white p-8 flex items-start gap-4">
          <Clock className="w-6 h-6 text-safety shrink-0" />
          <div>
            <div className="font-display text-xl tracking-tight text-forest">Under review</div>
            <p className="mt-2 text-sm text-muted-foreground">Submitted {new Date(pending.submitted_at).toLocaleString("en-GB")}. We typically review within 1 business day.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 border border-border bg-white p-6 lg:p-8 space-y-5">
          <div>
            <div className="overline text-muted-foreground mb-2">Proof of insurance *</div>
            <div className="border border-dashed border-border p-6">
              {docPath ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-forest" />
                    <div>
                      <div className="text-sm font-medium">{docName}</div>
                      <a href={fileUrl(docPath)} target="_blank" rel="noreferrer" className="text-xs text-safety overline">Preview →</a>
                    </div>
                  </div>
                  <button type="button" onClick={() => { setDocPath(""); setDocName(""); }} className="hover:text-safety" aria-label="Remove file">
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="grid place-items-center cursor-pointer">
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onFile} className="hidden" data-testid="verification-upload" />
                  <div className="flex flex-col items-center gap-2 text-muted-foreground py-4">
                    {uploading ? <Upload className="w-6 h-6 animate-pulse" /> : <Upload className="w-6 h-6" />}
                    <span className="overline text-xs">{uploading ? "Uploading…" : "Click to upload PDF or image (max 8MB)"}</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="overline text-muted-foreground mb-2">Insurance provider</div>
              <Input value={insurance} onChange={(e) => setInsurance(e.target.value)} placeholder="Hiscox, AXA…" data-testid="verification-insurer" className="rounded-none h-11" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2">Policy number</div>
              <Input value={policy} onChange={(e) => setPolicy(e.target.value)} data-testid="verification-policy" className="rounded-none h-11" />
            </div>
          </div>
          <div>
            <div className="overline text-muted-foreground mb-2">Companies House number (optional)</div>
            <Input value={chNumber} onChange={(e) => setChNumber(e.target.value)} placeholder="e.g. 01234567" data-testid="verification-ch" className="rounded-none h-11" />
          </div>

          <div className="pt-4 border-t border-border flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              Free to submit. £{Number(price).toFixed(2)}/year only charged after admin approval.
            </div>
            <Button
              type="submit"
              disabled={submitting || !docPath}
              data-testid="verification-submit"
              className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8"
            >
              {submitting ? "Submitting…" : "Submit for verification →"}
            </Button>
          </div>
        </form>
      )}

      {/* History */}
      {state.items?.length > 1 && (
        <div className="mt-10">
          <div className="overline text-muted-foreground mb-2">History</div>
          <div className="border border-border bg-white">
            {state.items.map((v, i) => (
              <div key={v.verification_id} className={`p-4 grid grid-cols-[auto,1fr,auto] gap-4 items-center ${i > 0 ? "border-t border-border" : ""}`}>
                <Badge className={`rounded-none overline ${v.status === "approved" || v.status === "active" ? "bg-forest text-white" : v.status === "rejected" ? "bg-destructive text-white" : v.status === "approved_awaiting_payment" ? "bg-safety text-white" : "bg-muted text-forest"}`}>
                  {v.status === "approved_awaiting_payment" ? "awaiting payment" : v.status}
                </Badge>
                <div className="text-sm">
                  <div>{v.insurance_provider || "Proof of insurance"}</div>
                  {v.admin_note && <div className="text-xs text-muted-foreground mt-1">Note: {v.admin_note}</div>}
                </div>
                <div className="text-xs text-muted-foreground">{new Date(v.submitted_at).toLocaleDateString("en-GB")}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
